import { useEffect, useState } from "react";
import PageTitle from "../components/PageTitle";
import { adminApi } from "../services/adminApi";
import AboutPageSettingsForm from "./AboutPageSettingsForm";
import BrandingSettingsForm from "./BrandingSettingsForm";
import ContactPageSettingsForm from "./ContactPageSettingsForm";
import GeneralSettingsForm from "./GeneralSettingsForm";
import SeoSettingsForm from "./SeoSettingsForm";

const SETTINGS_TABS = [
  { id: "general", label: "General" },
  { id: "branding", label: "Branding" },
  { id: "about", label: "About Page" },
  { id: "contact", label: "Contact Page" },
  { id: "seo", label: "SEO" },
];
const IMAGE_FIELD_NAMES = ["logo", "about_image", "about_secondary_image"];
const READ_ONLY_FIELD_NAMES = ["created_at", "updated_at"];

function appendSettingsValue(formData, key, value) {
  if (value === undefined || value === null || READ_ONLY_FIELD_NAMES.includes(key)) {
    return;
  }

  if (IMAGE_FIELD_NAMES.includes(key)) {
    return;
  }

  formData.append(key, value);
}

function SettingsPage() {
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState(null);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    adminApi("/site-settings/")
      .then((payload) => {
        setSettings(payload);
        setError("");
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  function updateSetting(name, value) {
    setSettings((currentSettings) => ({
      ...currentSettings,
      [name]: value,
    }));
  }

  function updateFile(name, file) {
    setFiles((currentFiles) => ({
      ...currentFiles,
      [name]: file,
    }));
  }

  async function saveSettings(event) {
    event.preventDefault();
    setSaving(true);
    setSuccess("");
    setError("");

    const formData = new FormData();
    Object.entries(settings).forEach(([key, value]) => appendSettingsValue(formData, key, value));
    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    try {
      const payload = await adminApi("/site-settings/", {
        method: "PATCH",
        body: formData,
      });
      setSettings(payload);
      setFiles({});
      setSuccess("Settings saved.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  function renderActiveTab() {
    if (!settings) {
      return null;
    }

    if (activeTab === "branding") {
      return <BrandingSettingsForm settings={settings} onChange={updateSetting} onFileChange={updateFile} />;
    }

    if (activeTab === "about") {
      return <AboutPageSettingsForm settings={settings} onChange={updateSetting} onFileChange={updateFile} />;
    }

    if (activeTab === "contact") {
      return <ContactPageSettingsForm settings={settings} onChange={updateSetting} />;
    }

    if (activeTab === "seo") {
      return <SeoSettingsForm settings={settings} onChange={updateSetting} />;
    }

    return <GeneralSettingsForm settings={settings} onChange={updateSetting} />;
  }

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Settings" title="Public Site Settings">
        Manage the public About and Contact pages, branding, and SEO for Zayas Kitchen.
      </PageTitle>

      <section className="card admin-panel">
        <header className="card-header admin-settings-header">
          <div className="menu-category-tabs" role="tablist" aria-label="Settings sections">
            {SETTINGS_TABS.map((tab) => (
              <button
                aria-selected={activeTab === tab.id}
                className="menu-category-tab"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap">
            <a className="btn btn-sm btn-light" href="/about" target="_blank" rel="noreferrer">
              Preview About
            </a>
            <a className="btn btn-sm btn-light" href="/contact" target="_blank" rel="noreferrer">
              Preview Contact
            </a>
          </div>
        </header>

        <form className="card-body flex flex-column gap-4" onSubmit={saveSettings}>
          {loading ? <div className="empty-state">Loading settings...</div> : null}
          {error ? <div className="alert alert-error">{error}</div> : null}
          {success ? <div className="alert alert-success">{success}</div> : null}
          {renderActiveTab()}
          <button className="btn btn-primary" disabled={!settings || saving} type="submit">
            {saving ? "Saving" : "Save Settings"}
          </button>
        </form>
      </section>
    </main>
  );
}

export default SettingsPage;